"use client";

import { Suspense, useState } from "react";
import { useSearchParams } from "next/navigation";
import { flatEvents, useV1EventWindow, useV1Events } from "@/lib/hooks/useEvents";
import { useCurrentUser } from "@/lib/hooks/useCurrentUser";
import { useTitle } from "@/lib/hooks/useTitle";
import { EventCard } from "@/components/EventCard";
import { EventComposer, type EventDraft } from "@/components/EventComposer";
import type { EntityId } from "@theta/api-client";
import { CalendarView } from "@/components/CalendarView";

/**
 * ONE COLLECTION, FOUR LEGACY LISTINGS (§2.8 #66-68)
 *
 * `GET /theta/events`, `/theta/events/public`, `/theta/groups/:id/events` and
 * the `?year=&month=` calendar mode are `GET /events` with `?group=`, `?from=`,
 * `?to=` and `?past=`. The calendar asks for the month it draws — an ISO
 * WINDOW, so a week view would cost nothing — instead of a bespoke mode.
 *
 * THE "MINE / OFFENTLIGE" TOGGLE IS GONE, and it is a deliberate narrowing
 * rather than an oversight — see `blockedOn`. Legacy `/theta/events` was
 * "everything I may see" and `/theta/events/public` was "events not marked
 * private"; v1's `/events` is the first of those and has no filter for the
 * second, so the toggle had one arm left to point at. Anonymous callers are
 * unaffected: a signed-out viewer only ever saw the public slice, and
 * `eventVisible` still gives exactly that.
 *
 * The month window is built in LOCAL time and sent as instants, which is what
 * `?year=&month=` meant all along.
 */
function monthWindow(year: number, month: number): { from: string; to: string } {
  return {
    from: new Date(year, month - 1, 1).toISOString(),
    to: new Date(year, month, 1).toISOString(),
  };
}

/**
 * `useSearchParams()` opts a page out of static prerendering unless it sits
 * inside a Suspense boundary — `next build` refuses the page outright
 * otherwise, which is how this was found (the typecheck and the unit tests
 * cannot see it; only a production build can). Same shape as `login/page.tsx`,
 * which reads the query string for the same reason.
 */
export default function EventsPage() {
  return (
    <Suspense>
      <EventsPageInner />
    </Suspense>
  );
}

function EventsPageInner() {
  useTitle("Begivenheder");
  const { data: currentUser } = useCurrentUser();
  const [showPast, setShowPast] = useState(false);
  /**
   * A DRAFT ARRIVING BY LINK — the one the bot's `propose_event` tool hands a
   * member. The bot creates nothing; it produces this URL, and the person
   * clicking it lands in the ordinary form with the fields seeded, under their
   * own permissions. See `backend/src/modules/llm-bot/tools/propose-event.ts`.
   *
   * The params are read once into state rather than watched: this seeds a form
   * somebody is about to type in, so re-reading the URL under them — on any
   * re-render, or after a back-navigation — would overwrite what they had
   * changed. First render wins, and after that the form is theirs.
   */
  const search = useSearchParams();
  const [draft] = useState<EventDraft | undefined>(() => {
    if (search.get("opret") !== "1") return undefined;
    const value = (k: string) => search.get(k)?.trim() || undefined;
    return {
      title: value("titel"),
      description: value("beskrivelse"),
      location: value("sted"),
      startsAt: value("start"),
      endsAt: value("slut"),
    };
  });
  // Cast at the boundary, which is what the branded id is for and how the rest
  // of the app crosses it (see `useAttachments`). A query string is untrusted
  // input, so the shape is checked FIRST — anything that is not a positive
  // integer is dropped rather than handed on as an id.
  const [draftGroupId] = useState<EntityId<"group"> | undefined>(() => {
    const raw = search.get("gruppe");
    const n = raw ? Number(raw) : NaN;
    return Number.isInteger(n) && n > 0 ? (n as EntityId<"group">) : undefined;
  });

  const [composerOpen, setComposerOpen] = useState(draft !== undefined);
  const [displayMode, setDisplayMode] = useState<"list" | "calendar">("list");

  const now = new Date();
  const [calYear, setCalYear] = useState(now.getFullYear());
  const [calMonth, setCalMonth] = useState(now.getMonth() + 1);

  const isCal = displayMode === "calendar";

  const listQuery = useV1Events({ past: showPast }, { enabled: !isCal });
  const events = flatEvents(listQuery.data);
  const isLoading = listQuery.isLoading;

  // THE WHOLE MONTH, not its first page — see `useV1EventWindow`. This asked
  // for `limit: 100` and never walked the cursor, so a month with more than a
  // hundred events drew empty days at the end of it and looked right.
  const calQuery = useV1EventWindow(monthWindow(calYear, calMonth), { enabled: isCal });
  const calEvents = calQuery.data ?? [];
  const calLoading = calQuery.isLoading;

  return (
    <div className="bg-surface rounded-xl border border-border p-4 sm:p-6">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground">
            Begivenheder
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Se hvad der sker i dine grupper og omkring dig
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-border overflow-hidden">
            <button
              onClick={() => setDisplayMode("list")}
              className={`p-1.5 transition-colors ${
                displayMode === "list"
                  ? "bg-indigo-100 dark:bg-indigo-950 text-primary-muted-foreground"
                  : "text-subtle-foreground hover:bg-muted"
              }`}
              aria-label="Listevisning"
              title="Listevisning"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="8" y1="6" x2="21" y2="6" />
                <line x1="8" y1="12" x2="21" y2="12" />
                <line x1="8" y1="18" x2="21" y2="18" />
                <line x1="3" y1="6" x2="3.01" y2="6" />
                <line x1="3" y1="12" x2="3.01" y2="12" />
                <line x1="3" y1="18" x2="3.01" y2="18" />
              </svg>
            </button>
            <button
              onClick={() => setDisplayMode("calendar")}
              className={`p-1.5 transition-colors ${
                displayMode === "calendar"
                  ? "bg-indigo-100 dark:bg-indigo-950 text-primary-muted-foreground"
                  : "text-subtle-foreground hover:bg-muted"
              }`}
              aria-label="Kalendervisning"
              title="Kalendervisning"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
            </button>
          </div>
          {currentUser && (
            <button
              onClick={() => setComposerOpen(true)}
              className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              <span className="hidden sm:inline">Opret begivenhed</span>
              <span className="sm:hidden">Opret</span>
            </button>
          )}
        </div>
      </div>

      {displayMode === "calendar" && (
        <CalendarView
          events={calEvents}
          year={calYear}
          month={calMonth}
          onMonthChange={(y, m) => {
            setCalYear(y);
            setCalMonth(m);
          }}
          isLoading={calLoading}
        />
      )}

      {displayMode === "list" && (
        <>
          <div className="flex gap-1 mb-4 border-b border-border">
            <button
              onClick={() => setShowPast(false)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                !showPast
                  ? "border-indigo-600 text-primary"
                  : "border-transparent text-muted-foreground hover:text-gray-700 dark:hover:text-gray-300"
              }`}
            >
              Kommende
            </button>
            <button
              onClick={() => setShowPast(true)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                showPast
                  ? "border-indigo-600 text-primary"
                  : "border-transparent text-muted-foreground hover:text-gray-700 dark:hover:text-gray-300"
              }`}
            >
              Tidligere
            </button>
          </div>

          {isLoading && (
            <div className="grid gap-4 sm:grid-cols-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="bg-surface rounded-xl border border-border overflow-hidden animate-pulse"
                >
                  <div className="h-40 bg-muted" />
                  <div className="p-4 space-y-2">
                    <div className="h-3 bg-muted rounded w-1/3" />
                    <div className="h-5 bg-muted rounded w-2/3" />
                    <div className="h-3 bg-muted rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {!isLoading && events.length > 0 && (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                {events.map((event) => (
                  <EventCard key={event.id} event={event} />
                ))}
              </div>
              {/* Cursor paging (§1.3) — the old list asked for `?page=1` and
                  had no way to ask for the second one. */}
              {listQuery.hasNextPage && (
                <div className="text-center mt-4">
                  <button
                    type="button"
                    onClick={() => listQuery.fetchNextPage()}
                    disabled={listQuery.isFetchingNextPage}
                    className="px-4 py-2 text-sm font-medium text-primary hover:underline disabled:opacity-50"
                  >
                    {listQuery.isFetchingNextPage ? "Indlæser..." : "Vis flere begivenheder"}
                  </button>
                </div>
              )}
            </>
          )}

          {!isLoading && events.length === 0 && (
            <div className="text-center py-12">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="mx-auto text-subtle-foreground mb-4">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              <h2 className="text-lg font-semibold text-foreground mb-1">
                {showPast ? "Ingen tidligere begivenheder" : "Ingen kommende begivenheder"}
              </h2>
              <p className="text-sm text-muted-foreground">
                {showPast
                  ? "Der er ingen tidligere begivenheder."
                  : "Opret den første begivenhed i en af dine grupper!"}
              </p>
            </div>
          )}
        </>
      )}

      {currentUser && (
        <EventComposer
          open={composerOpen}
          onClose={() => setComposerOpen(false)}
          draft={draft}
          defaultGroupId={draftGroupId}
        />
      )}
    </div>
  );
}
